import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { SectionForm, Divider, Link, Text, Icon, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import { ModalOptions } from '@Sage/ssm-web5';
import { useSelector } from 'react-redux';
import SelectHoi from 'components/SelectHoi';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { FuzzyMap } from 'utils/fuzzy';
import {
    ACTION_OPERATORS,
    ACTION_OPERATORS_DEFAULTS,
    ACTION_OPERATORS_NO_INPUT,
} from '../../../../../../utils';

import DateTime from 'containers/components/Fields/DateTime';
import InputTemplated from '../../../../components/InputTemplated';
import InputNDatesOperatorForm from './InputNDatesOperatorForm';
import InputWeekDayOperatorForm from './InputWeekDayOperatorForm';
import AdvancedSelect from 'components/AdvancedSelect';
import Select from 'containers/components/Fields/Select';
import SelectFuzzy from 'containers/components/Fields/SelectFuzzy';
import MultiSelect from 'containers/components/Fields/MultiSelect';
import MultiSelectFuzzy from 'containers/components/Fields/MultiSelectFuzzy';
import AbsoluteEmpty from 'components/AbsoluteEmpty';
import CustomizationIllustration from 'components/illustrations/CustomizationIllustration';
import TimezoneSelector from '../../../../components/InputTemplated/TimezoneSelector';

import './styles.scss';

const UpdateEntityRelatedAction = memo(({ data, onChange, form, action, variables }) => {
    const theme = useTheme();
    const userTimezone = useSelector((state) => parseInt(state.config?.userData?.idTimeZone, 10));
    const [fields, setFields] = useState([]);
    const [operators, setOperators] = useState({});
    const [modalFieldsOpen, setModalFieldsOpen] = useState(false);
    const [selectedFields, setSelectedFields] = useState({});
    const formEntity = useRef();
    const inputTemplatedProps = useRef({});
    const disabledFields = useRef({});
    const boolOptions = useMemo(
        () => [
            {
                label: getLiteral('cfm_label_yes'),
                value: true,
            },
            {
                label: getLiteral('cfm_label_no'),
                value: false,
            },
        ],
        [],
    );

    useEffect(() => {
        if (!variables) return;
        let fields = variables?.values?.fields[form.parameters?.entity?.entity];
        setFields(
            fields?.map((item) => {
                disabledFields.current[item.name] = { options: [] };
                return {
                    label: item.name,
                    id: item.name,
                    options:
                        item.fields?.map?.((field) => {
                            const checkMandatories =
                                form.name === 'create_entity' && field.mandatory;
                            if (checkMandatories) {
                                disabledFields.current[item.name].options.push(field.key);
                            }
                            return {
                                ...field,
                                label: getLiteral(field.literal) + (checkMandatories ? ' *' : ''),
                                id: field.key,
                            };
                        }) || [],
                };
            }) || [],
        );

        setOperators(variables?.operators);

        let options = [];
        variables?.values?.variables?.entity?.sections?.forEach((section) => {
            options.push({
                label: section.name,
                key: variables?.values?.variables?.entity?.value,
                options: section.fields.map((field) => ({
                    label: getLiteral(field.literal),
                    value: field.value,
                    type: field.type,
                    table: field.table,
                })),
            });
        });

        const filterOptions = variables?.values?.variables?.related?.reduce(
            (acc, item) => {
                acc.push({
                    label: getLiteral(item.description),
                    value: item.value,
                    iconType: item.entity,
                });

                item.sections?.forEach((section) => {
                    options.push({
                        label: section.name,
                        key: item.value,
                        options: section.fields.map((field) => ({
                            label: getLiteral(field.literal),
                            value: field.value,
                            type: field.type,
                            table: field.table,
                        })),
                    });
                });
                return acc;
            },
            [
                {
                    label: getLiteralWithParameters(
                        variables?.values?.variables?.entity?.description,
                        [getLiteral(data?.scene?.parameters?.entity?.description)],
                    ),
                    value: variables?.values?.variables?.entity?.value,
                    iconType: variables?.values?.variables?.entity?.entity,
                },
            ],
        );

        inputTemplatedProps.current = {
            filterOptions,
            options,
        };
    }, [variables, form, data?.scene?.parameters?.entity?.description]);

    useEffect(() => {
        if (
            (form?.parameters?.entity !== formEntity.current || !formEntity.current) &&
            fields?.length > 0
        ) {
            formEntity.current = form?.parameters?.entity;

            let variablesFields = variables?.values?.fields[form.parameters?.entity?.entity];

            if (form.fields?.length > 0) {
                const newSelectedFields = {};
                const fieldsBySectionOrdered = {};
                const sectionOrder = [];

                form.fields.forEach((field) => {
                    const fieldKey = field.key;
                    variablesFields?.forEach((section) => {
                        const foundField = section.fields?.find((f) => f.key === fieldKey);
                        if (foundField) {
                            if (!fieldsBySectionOrdered[section.name]) {
                                fieldsBySectionOrdered[section.name] = [];
                                sectionOrder.push(section.name);
                            }
                            fieldsBySectionOrdered[section.name].push(fieldKey);
                        }
                    });
                });

                sectionOrder.forEach((sectionName) => {
                    const sectionId = variablesFields?.find(
                        (section) => section.name === sectionName,
                    )?.name;
                    if (sectionId) {
                        newSelectedFields[sectionId] = {
                            isSelected: false,
                            options: fieldsBySectionOrdered[sectionName],
                        };
                    }
                });

                setSelectedFields(newSelectedFields);
            } else if (form.name === 'create_entity') {
                if (variables?.values?.fields) {
                    const mandatoryFields = {};
                    const entityFields =
                        variables?.values?.fields[form.parameters?.entity?.entity] || [];

                    entityFields.forEach((section) => {
                        const mandatoryOptions =
                            section.fields
                                ?.filter((field) => field.mandatory)
                                .map((field) => field.key) || [];

                        if (mandatoryOptions.length > 0) {
                            mandatoryFields[section.name] = {
                                isSelected: false,
                                options: mandatoryOptions,
                            };
                        }
                    });

                    if (Object.keys(mandatoryFields).length > 0) {
                        setTimeout(() => onSelectFields(mandatoryFields, false));
                    }
                }
            }
        }
    }, [
        fields,
        form,
        form.name,
        form.parameters?.entity,
        onSelectFields,
        variables?.values?.fields,
    ]);

    const changeValues = useCallback(
        (field) => (value) => {
            onChange((current) => {
                return {
                    ...current,
                    values: {
                        ...(current?.values || {}),
                        [field]: value,
                    },
                };
            });
        },
        [onChange],
    );

    const changeFormat = useCallback(
        (formatField, field) => (value) => {
            onChange((current) => {
                return {
                    ...current,
                    format: {
                        ...(current?.format || {}),
                        [field]: { ...(current?.format?.[field] || {}), [formatField]: value },
                    },
                };
            });
        },
        [onChange],
    );

    const changeOperator = useCallback(
        (field) => (value) => {
            onChange((current) => {
                return {
                    ...current,
                    operators: { ...current.operators, [field]: value },
                    values: {
                        ...(current?.values || {}),
                        [field]: undefined,
                    },
                };
            });
        },
        [onChange],
    );

    const getCalculatedSelectFields = useCallback((field) => {
        const filteredOptions = inputTemplatedProps.current?.options
            ?.map((section) => ({
                ...section,
                options: section.options?.filter(
                    (option) =>
                        option.type === field.type &&
                        option.table?.table?.toLowerCase?.() ===
                            field.table?.table?.toLowerCase?.(),
                ),
            }))
            .filter((section) => section.options?.length > 0);

        const filteredFilterOptions = inputTemplatedProps.current?.filterOptions?.filter((filter) =>
            filteredOptions?.some((section) => section.key === filter.value),
        );

        return {
            options: filteredOptions || [],
            filterOptions: filteredFilterOptions || [],
        };
    }, []);

    const getSelectFieldsByTypes = useCallback((types) => {
        const filteredOptions = inputTemplatedProps.current?.options
            ?.map((section) => ({
                ...section,
                options: section.options?.filter((option) => !types || types.includes(option.type)),
            }))
            .filter((section) => section.options?.length > 0);

        const filteredFilterOptions = inputTemplatedProps.current?.filterOptions?.filter((filter) =>
            filteredOptions?.some((section) => section.key === filter.value),
        );

        return {
            options: filteredOptions || [],
            filterOptions: filteredFilterOptions || [],
        };
    }, []);

    const getFieldByType = useCallback(
        (field) => {
            let list, listProps, Component;
            let calculatorOptions, calculatedOptions;
            switch (form?.operators?.[field.value]) {
                case 'calculated_string':
                    calculatorOptions = getSelectFieldsByTypes();
                    return (
                        <InputTemplated
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            placeholder={getLiteral(
                                'label_automation_calculated_value_text_placeholder',
                            )}
                            isFullWidth
                            insertFieldOptions={calculatorOptions}
                            locale={form?.format?.[field.value]?.locale}
                            timeZone={form?.format?.[field.value]?.timeZone}
                            onChangeLocale={changeFormat('locale', field.value)}
                            onChangeTimeZone={changeFormat('timeZone', field.value)}
                        />
                    );
                case 'calculated_calculator_int':
                case 'calculated_calculator_float':
                    calculatorOptions = getSelectFieldsByTypes([
                        'currency',
                        'decimal',
                        'int',
                        'percentage',
                    ]);

                    // Checking valid characters
                    const error =
                        form?.values?.[field.value] &&
                        !form?.values?.[field.value]?.match(
                            /^(?:[0-9+\-/*()%#\s._]|{{[a-z0-9._\s]+}})*$/gi,
                        )
                            ? getLiteral('error_automation_calculated_value_number')
                            : undefined;

                    return (
                        <InputTemplated
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            placeholder={getLiteral(
                                'label_automation_calculated_value_number_placeholder',
                            )}
                            error={error}
                            isNumber
                            isFullWidth
                            insertFieldOptions={calculatorOptions}
                        />
                    );
                case 'calculated_datetime':
                case 'calculated_boolean':
                case 'calculated_single_list':
                case 'calculated_multi_list':
                    calculatedOptions = getCalculatedSelectFields(field);
                    // Get the entity label for the selected value
                    let calculatedValue = form?.values?.[field.value];
                    if (
                        calculatedValue &&
                        typeof calculatedValue === 'object' &&
                        calculatedValue.value
                    ) {
                        // Extract entity from template format {{ .entity.field }}
                        // Get all segments except the last one
                        const match = calculatedValue.value.match(/\{\{\s*\.(.+?)\s*\}\}/);
                        let entityKey = null;
                        if (match) {
                            const segments = match[1].split('.');
                            // Remove the last segment (the field name)
                            segments.pop();
                            entityKey = segments.join('.');
                        }

                        let valueLabel = calculatedValue.label;
                        if (entityKey) {
                            const entityInfo = inputTemplatedProps.current?.filterOptions?.find(
                                (filter) => filter.value === entityKey,
                            );
                            if (entityInfo) {
                                valueLabel = `${calculatedValue.label} (${entityInfo.label})`;
                            } else if (entityKey === 'event.data') {
                                valueLabel = `${calculatedValue.label} (${getLiteralWithParameters(
                                    'label_automation_entity_trigger',
                                    [getLiteral(data?.scene?.parameters?.entity?.description)],
                                )})`;
                            }
                        }

                        calculatedValue = {
                            ...calculatedValue,
                            label: valueLabel,
                        };
                    }
                    return (
                        <AdvancedSelect
                            filterPlaceholder={getLiteral('label_placeholder_entity')}
                            searchPlaceholder={getLiteral('placeholder_search_field')}
                            menuTitle={getLiteral('cfm_action_add_field')}
                            placeholder={getLiteral('label_placeholder_field')}
                            onChange={changeValues(field.value)}
                            options={calculatedOptions.options}
                            defaultFilter={calculatedOptions.filterOptions?.[0]?.value}
                            filterOptions={calculatedOptions.filterOptions}
                            value={calculatedValue}
                            isFullWidth
                            useMenuPortal={false}
                        />
                    );
                case 'specific_boolean':
                    return (
                        <SelectHoi
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            options={boolOptions}
                            usePlainValue
                            isFullWidth
                            useMenuPortal={false}
                        />
                    );

                case 'specific_datetime':
                    return (
                        <div
                            key={field.key}
                            className="fm-automation__update-entity-related__datetime-field"
                        >
                            <DateTime
                                onChange={changeValues(field.value)}
                                value={form?.values?.[field.value]}
                            />
                            <TimezoneSelector
                                onChange={changeFormat('timeZone', field.value)}
                                timeZone={form?.format?.[field.value]?.timeZone || userTimezone}
                            />
                        </div>
                    );
                case 'days_after_datetime':
                case 'week_after_datetime':
                case 'month_after_datetime':
                    calculatedOptions = getCalculatedSelectFields(field);
                    return (
                        <InputNDatesOperatorForm
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            options={calculatedOptions.options}
                            defaultFilter={calculatedOptions.filterOptions?.[0]?.value}
                            filterOptions={calculatedOptions.filterOptions}
                            triggerEntityDescription={data?.scene?.parameters?.entity?.description}
                        />
                    );
                case 'next_day_week_datetime':
                    calculatedOptions = getCalculatedSelectFields(field);
                    return (
                        <InputWeekDayOperatorForm
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            options={calculatedOptions.options}
                            defaultFilter={calculatedOptions.filterOptions?.[0]?.value}
                            filterOptions={calculatedOptions.filterOptions}
                            triggerEntityDescription={data?.scene?.parameters?.entity?.description}
                        />
                    );
                case 'specific_single_list':
                    list = field.table?.table;
                    listProps = FuzzyMap[list?.toLowerCase()] || list || {};
                    Component = listProps?.list ? SelectFuzzy : Select;
                    return (
                        <Component
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            cacheOptions={false}
                            list={field?.table?.table}
                            {...listProps}
                            useMenuPortal={false}
                            usePlainValue
                        />
                    );
                case 'specific_multi_list':
                    list = field.table?.table;
                    listProps = FuzzyMap[list?.toLowerCase()] || list || {};
                    Component = listProps?.list ? MultiSelectFuzzy : MultiSelect;
                    return (
                        <Component
                            key={field.key}
                            onChange={changeValues(field.value)}
                            value={form?.values?.[field.value]}
                            list={field?.table?.table}
                            {...listProps}
                            useMenuPortal={false}
                            usePlainValue
                        />
                    );
                default:
                    return null;
            }
        },
        [
            boolOptions,
            changeFormat,
            changeValues,
            form?.format,
            form?.operators,
            form?.values,
            getCalculatedSelectFields,
            getSelectFieldsByTypes,
            userTimezone,
            data?.scene?.parameters?.entity?.description,
        ],
    );

    const showFieldsModal = useCallback(() => {
        setModalFieldsOpen(true);
    }, []);

    const handleOnClose = useCallback(() => {
        setModalFieldsOpen(false);
    }, []);

    const onChangeSelectedFields = useCallback((selected) => {
        setSelectedFields(selected);
    }, []);

    const onSelectFields = useCallback(
        (selected, touched = true) => {
            if (!fields) return;

            const fieldsMap = fields.reduce((obj, section) => {
                section.options.forEach((option) => (obj[option.key] = option));
                return obj;
            }, {});

            const fieldsList = Object.entries(selected).reduce((obj, [section, value]) => {
                value.options.forEach((option) => {
                    const field = fieldsMap[option];
                    obj.push({
                        ...field,
                        section,
                        label: getLiteral(field?.literal),
                        value: field?.key,
                    });
                });
                return obj;
            }, []);

            onChange((current) => {
                return {
                    ...current,
                    fields: fieldsList,
                    operators: {
                        ...current.operators,
                        ...fieldsList?.reduce((obj, value) => {
                            if (current.operators?.[value?.value]) return obj;
                            else {
                                obj[value?.value] = ACTION_OPERATORS_DEFAULTS[value?.type];
                            }
                            return obj;
                        }, {}),
                    },
                };
            }, touched);

            setModalFieldsOpen(false);

            setSelectedFields(selected);
        },
        [fields, onChange],
    );

    const fieldsForm = useMemo(() => {
        const groupedFields = [];
        const sectionsMap = {};

        form?.fields?.forEach((field) => {
            const section = field.section
                ? field.section
                : fields?.find((section) =>
                      section.options?.find((option) => option.key === field.key),
                  )?.label || '';

            if (!sectionsMap[section]) {
                sectionsMap[section] = [];
                groupedFields.push([section, sectionsMap[section]]);
            }
            sectionsMap[section].push(field);
        });

        return groupedFields.map(([section, fields]) => (
            <SectionForm key={section} title={section}>
                <div className="fm-automation__update-entity-related__fields">
                    {fields.map((field) => {
                        const operatorOptions = operators?.[field.type]?.map((operator) => ({
                            label: getLiteral(ACTION_OPERATORS[operator]),
                            value: operator,
                        }));

                        const hasInput = !ACTION_OPERATORS_NO_INPUT.includes(
                            form?.operators?.[field.value],
                        );

                        const isMandatory = form.name === 'create_entity' && field.mandatory;

                        return (
                            <div
                                key={field.key}
                                className="fm-automation__update-entity-related__fields-form"
                            >
                                <div className="fm-automation__update-entity-related__fields-form__title">
                                    <Text>
                                        {getLiteral(field.literal)} {isMandatory ? '*' : null}
                                    </Text>
                                    {!isMandatory && (
                                        <Tooltip
                                            content={getLiteral('action_delete_filed')}
                                            placement="topRight"
                                        >
                                            <span>
                                                <Icon
                                                    name="less"
                                                    size="large"
                                                    color={theme.colors.actionMinor[500]}
                                                    onClick={() => {
                                                        onChange((current) => {
                                                            const fieldsList =
                                                                current?.fields?.filter(
                                                                    (f) => f.value !== field.value,
                                                                );

                                                            const updatedSelectedFields = {
                                                                ...selectedFields,
                                                            };
                                                            Object.keys(
                                                                updatedSelectedFields,
                                                            ).forEach((sectionKey) => {
                                                                const section =
                                                                    updatedSelectedFields[
                                                                        sectionKey
                                                                    ];
                                                                const newOptions =
                                                                    section.options.filter(
                                                                        (option) =>
                                                                            option !== field.key,
                                                                    );

                                                                if (newOptions.length === 0) {
                                                                    delete updatedSelectedFields[
                                                                        sectionKey
                                                                    ];
                                                                } else {
                                                                    updatedSelectedFields[
                                                                        sectionKey
                                                                    ] = {
                                                                        ...section,
                                                                        options: newOptions,
                                                                    };
                                                                }
                                                            });
                                                            setSelectedFields(
                                                                updatedSelectedFields,
                                                            );

                                                            return {
                                                                ...current,
                                                                fields: fieldsList,
                                                                operators: {
                                                                    ...current.operators,
                                                                    ...fieldsList?.reduce(
                                                                        (obj, value) => {
                                                                            if (
                                                                                current.operators?.[
                                                                                    value?.value
                                                                                ]
                                                                            )
                                                                                return obj;
                                                                        },
                                                                        {},
                                                                    ),
                                                                },
                                                            };
                                                        });
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                                <SelectHoi
                                    options={operatorOptions}
                                    value={form?.operators?.[field.value]}
                                    onChange={changeOperator(field.value)}
                                    useMenuPortal={false}
                                    isFullWidth
                                    usePlainValue
                                />
                                {hasInput && getFieldByType(field)}
                            </div>
                        );
                    })}
                </div>
            </SectionForm>
        ));
    }, [
        form?.fields,
        form?.operators,
        form?.name,
        operators,
        theme.colors.actionMinor,
        changeOperator,
        getFieldByType,
        onChange,
        selectedFields,
        fields,
    ]);

    const sectionTitle = useMemo(() => {
        if (!form?.parameters?.entity) return;
        return `${action?.label} ${form?.parameters?.entity?.label}`;
    }, [action?.label, form]);

    const modalOptions = useMemo(() => {
        return {
            overlayClassName: 'fm-automation__update-entity-related-action__modal-overlay',
            title: getLiteral('cfm_action_add_field'),
            schema: fields,
            disabled: disabledFields.current,
            selected: selectedFields,
            isOpen: modalFieldsOpen,
            onChangeSelected: onChangeSelectedFields,
            placeholderSearch: getLiteral('label_search_field'),
            confirmText: getLiteral('action_save'),
            onConfirm: onSelectFields,
            cancelText: getLiteral('action_cancel'),
            onCancel: handleOnClose,
        };
    }, [
        fields,
        handleOnClose,
        modalFieldsOpen,
        onChangeSelectedFields,
        onSelectFields,
        selectedFields,
    ]);

    const showFields = form?.fields?.length > 0;

    return (
        <>
            {form?.parameters?.entity && (
                <SectionForm title={sectionTitle} isExpandable={false}>
                    <div className="fm-automation__update-entity-related-action">
                        {!showFields && (
                            <AbsoluteEmpty
                                svg={<CustomizationIllustration />}
                                title={getLiteral('label_automations_action_update_select_field')}
                                subtitle={getLiteral(
                                    'label_automations_action_update_select_field_desc',
                                )}
                                size="small"
                            />
                        )}
                        {fieldsForm}
                        <Divider />
                        <div className="fm-automation__update-entity-related-action__footer">
                            <Link variation="primary" onClick={showFieldsModal} bold>
                                + {getLiteral('cfm_action_add_field')}
                            </Link>
                        </div>
                    </div>
                    <ModalOptions {...modalOptions} />
                </SectionForm>
            )}
        </>
    );
});

UpdateEntityRelatedAction.getInitialData = (node, data) => {
    return {
        ...node,
        name: node?.parameters?.entity ? node.name : undefined,
        parameters: {
            ...node.parameters,
            entity: node?.parameters?.entity
                ? {
                      label: getLiteralWithParameters(node.parameters.entity?.literal, [
                          getLiteral(data?.scene?.parameters?.entity?.description),
                      ]),
                      literal: node.parameters.entity?.literal,
                      value: node.parameters.entity?.value,
                      iconType: node.parameters.entity?.entity,
                      entity: node.parameters.entity?.entity,
                  }
                : undefined,
        },
        fields: node.parameters?.values?.map((value) => ({
            ...value?.field,
            label: getLiteral(value?.field?.literal),
            value: value?.field?.key,
        })),
        operators: node.parameters?.values?.reduce((obj, value) => {
            obj[value.field?.key] = value?.value?.type;
            return obj;
        }, {}),
        values: node.parameters?.values?.reduce((obj, value) => {
            obj[value.field?.key] = value?.value?.value;
            return obj;
        }, {}),
        format: node.parameters?.values?.reduce((obj, value) => {
            obj[value.field?.key] = {
                timeZone: value?.format?.timeZone,
                locale: value?.format?.locale,
            };
            return obj;
        }, {}),
    };
};

UpdateEntityRelatedAction.hasEmptyFields = (form) => {
    if (!form.fields?.length) return true;
    return form.fields.some((field) => {
        const operatorExcluded = ACTION_OPERATORS_NO_INPUT.includes(form.operators[field.value]);
        const value = form.values[field.value];
        const valueIsUndefined = value === null || value === undefined;
        return !operatorExcluded && valueIsUndefined;
    });
};

UpdateEntityRelatedAction.getSaveData = (form) => {
    let values = [];
    if (form.fields?.length > 0) {
        values = form.fields.map((field) => ({
            field,
            value: {
                type: form.operators[field.value],
                value: form.values[field.value],
            },
            format: {
                timeZone: form.format?.[field.value]?.timeZone,
                locale: form.format?.[field.value]?.locale,
            },
        }));
    }

    return { entity: form.parameters.entity, values };
};

export default UpdateEntityRelatedAction;
